/**
 * Centralised snackbar/toast feedback. Wraps the app once near the root,
 * exposes a `useToast()` hook so any descendant can fire success/error/info
 * messages without instantiating its own `<Snackbar>` per surface.
 *
 * MUI's `<Snackbar>` is intentionally single-instance — stacking toasts
 * requires an external library. For Issue #10's scope (one mutation
 * outcome at a time) the single-instance design is fine: the latest
 * toast supersedes the previous one, which matches user intent (the
 * user only cares about the last action they took).
 */

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { Alert, Snackbar } from "@mui/material";
import type { AlertColor } from "@mui/material";

type ToastInput = { message: string; severity?: AlertColor; durationMs?: number };
type ToastState = ToastInput & { id: number };

type ToastContextValue = {
  show: (toast: ToastInput) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toast, setToast] = useState<ToastState | null>(null);

  const show = useCallback((input: ToastInput) => {
    // Stable per-toast id so the auto-hide timer attaches to the right
    // instance — MUI matches close events to the active id.
    setToast({
      id: Date.now(),
      severity: input.severity ?? "info",
      durationMs: input.durationMs ?? DEFAULT_DURATION_MS,
      message: input.message,
    });
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      showError: (message: string) => show({ message, severity: "error" }),
      showSuccess: (message: string) => show({ message, severity: "success" }),
    }),
    [show],
  );

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    // Don't dismiss on click-away — operators routinely click outside the
    // snackbar without intending to drop the message. Auto-hide and the
    // explicit close button cover the rest.
    if (reason === "clickaway") return;
    setToast(null);
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        key={toast?.id ?? "empty"}
        open={Boolean(toast)}
        autoHideDuration={toast?.durationMs ?? DEFAULT_DURATION_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        data-testid="toast"
      >
        {toast ? (
          <Alert
            onClose={() => setToast(null)}
            severity={toast.severity ?? "info"}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}
